/**
 * WeblabGroupPanel — aggregate summary shown when the user clicks on a
 * Weblab ID group row (level 1 in the tree).
 *
 * Matches the tracker's DetailsPanelWeblab.tsx: shows weblab header info,
 * basic metadata, marketplace flags, and summed impact totals across all
 * treatments/marketplaces for this weblab.
 */

import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Link,
  List,
  ListItem,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import type { WeblabRow } from "./weblabColumns";
import { currencyFormatter, numberFormatter } from "./weblabFormatters";

interface WeblabGroupPanelProps {
  /** All leaf rows belonging to this weblab ID group. */
  rows: WeblabRow[];
  /** The weblab_id (e.g. "SB_SEARCH_ECTR_BRAND_TSDIN_1370712"). */
  weblabId: string;
  /** Optional treatment label (e.g. "C:T1") — shown when a treatment group is clicked. */
  treatment?: string;
}

/** Marketplace code → flag emoji (ISO 3166-1). */
const MARKETPLACE_FLAGS: Record<string, string> = {
  US: "🇺🇸",
  UK: "🇬🇧",
  DE: "🇩🇪",
  JP: "🇯🇵",
  FR: "🇫🇷",
  IT: "🇮🇹",
  ES: "🇪🇸",
  CA: "🇨🇦",
  IN: "🇮🇳",
  AU: "🇦🇺",
  MX: "🇲🇽",
  BR: "🇧🇷",
  NL: "🇳🇱",
  SE: "🇸🇪",
  PL: "🇵🇱",
  BE: "🇧🇪",
  SG: "🇸🇬",
  AE: "🇦🇪",
  SA: "🇸🇦",
  EG: "🇪🇬",
  TR: "🇹🇷",
};

/** Impact metric definitions — same order and formatting as the Summary panel. */
const IMPACT_METRICS: {
  label: string;
  field: string;
  isCount: boolean;
}[] = [
  { label: "SP Revenue", field: "sp_revenue_impact", isCount: false },
  { label: "OPS", field: "ops_impact", isCount: false },
  { label: "GCCP", field: "gccp_impact", isCount: false },
  { label: "Net CP", field: "net_cp_impact", isCount: false },
  { label: "SP Clicks", field: "sp_clicks_impact", isCount: true },
  { label: "Paid Units", field: "paid_units_impact", isCount: true },
  { label: "CSales", field: "csales_impact", isCount: false },
  {
    label: "SP Attr. OPS (1-day)",
    field: "sp_attributed_ops_1day",
    isCount: false,
  },
  { label: "SB Revenue", field: "sb_revenue_impact", isCount: false },
  { label: "SB Clicks", field: "sb_clicks_impact", isCount: true },
];

function buildPhoneToolLinks(owners: string | undefined | null) {
  if (!owners) return "—";
  return owners.split(",").map((alias, idx) => {
    const trimmed = alias.trim();
    return (
      <span key={trimmed}>
        {idx > 0 && ", "}
        <Link
          href={`https://phonetool.amazon.com/users/${trimmed}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {trimmed}
        </Link>
      </span>
    );
  });
}

const WeblabGroupPanel: React.FC<WeblabGroupPanelProps> = ({
  rows,
  weblabId,
  treatment,
}) => {
  // Use the first row for shared info (these are the same across all marketplaces)
  const firstRow = rows[0];

  // Aggregate impact totals across all rows in this weblab group
  const totals = useMemo(() => {
    const sums: Record<string, number> = {};
    for (const m of IMPACT_METRICS) sums[m.field] = 0;
    for (const r of rows) {
      for (const m of IMPACT_METRICS) {
        const v = parseFloat(r[m.field] as string);
        if (!isNaN(v)) sums[m.field] += v;
      }
    }
    return sums;
  }, [rows]);

  // Collect unique marketplaces with counts
  const marketplaces = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const mp = (r.marketplace_id ?? r.marketplace ?? "").toString().trim();
      if (mp) map.set(mp, (map.get(mp) ?? 0) + 1);
    }
    return map;
  }, [rows]);

  if (!rows || rows.length === 0) return null;

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 1 }}>
        <Link
          href={`https://weblab.amazon.com/wl/${weblabId}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: 700 }}
        >
          {weblabId}
        </Link>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
          {firstRow.short_description ?? ""}
          {firstRow.short_description ? " | " : ""}
          {firstRow.deep_dive_link && (
            <>
              <Link
                href={firstRow.deep_dive_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Deep Dive Link
              </Link>
              {" | "}
            </>
          )}
          <Link
            href={`https://w.amazon.com/bin/view/Weblab/BarRaiser/${weblabId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Bar Raiser Wiki
          </Link>
        </Typography>
        {treatment && (
          <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
            Baseline Treatment: {treatment.split(":")[0]} &nbsp;|&nbsp;
            Experiment Treatment: {treatment.split(":")[1]}
          </Typography>
        )}
        {firstRow.description && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {firstRow.description}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Section 1: Basic info */}
        <Grid item xs={12} sm={5}>
          <Paper variant="outlined" sx={{ p: 1 }}>
            <List dense disablePadding>
              {[
                {
                  label: "Sub Team",
                  value: firstRow.sub_team ?? firstRow.weblab_team ?? "—",
                },
                {
                  label: "Owners",
                  value: buildPhoneToolLinks(firstRow.owners),
                },
                {
                  label: "Experiment Type",
                  value: firstRow.experiment_type ?? "—",
                },
                { label: "Page Type", value: firstRow.page_type ?? "—" },
              ].map((item) => (
                <ListItem key={item.label} divider sx={{ py: 0.5 }}>
                  <Grid container>
                    <Grid item xs={5}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" component="div">
                        {item.value}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Marketplace flags */}
          {marketplaces.size > 0 && (
            <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {Array.from(marketplaces.entries()).map(([mp, count]) => (
                  <Tooltip key={mp} title={`${mp} (${count})`}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography variant="body2">
                        {MARKETPLACE_FLAGS[mp] ?? "🌐"}
                      </Typography>
                      <Typography variant="caption" fontWeight={500}>
                        {mp}
                      </Typography>
                      {count > 1 && (
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: "primary.main",
                            color: "white",
                            borderRadius: "50%",
                            width: 16,
                            height: 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.6rem",
                          }}
                        >
                          {count}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Section 2: Impact totals */}
        <Grid item xs={12} sm={7}>
          <Paper variant="outlined" sx={{ p: 1 }}>
            <List dense disablePadding>
              {/* Header row */}
              <ListItem divider sx={{ py: 0.5 }}>
                <Grid container>
                  <Grid item xs={5} />
                  <Grid item xs={7}>
                    <Typography variant="body2" fontWeight={600}>
                      Impact
                    </Typography>
                  </Grid>
                </Grid>
              </ListItem>
              {IMPACT_METRICS.map((m) => {
                const val = totals[m.field];
                const formatted =
                  val === 0
                    ? "—"
                    : m.isCount
                    ? numberFormatter(val)
                    : currencyFormatter(val);
                return (
                  <ListItem key={m.field} divider sx={{ py: 0.5 }}>
                    <Grid container>
                      <Grid item xs={5}>
                        <Typography variant="body2" fontWeight={600}>
                          {m.label}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2">{formatted}</Typography>
                      </Grid>
                    </Grid>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default WeblabGroupPanel;
